import {EventEmitter, Injectable} from '@angular/core';
import { ApiClientService } from './api-client.service';
import { StripeTransaction} from '../types/data.types';
import { UrlService } from './url.service';
import { EmptyRowService } from './empty-row.service';
import { AccountPermissionsService } from './account-permissions.service';
import { BuildingApiService } from './api/building-api.service';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { AmenityApiService } from './api/amenity-api.service';
import { IStripeAccount } from '../types/api-response/stripe-account';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { ApiResponseList } from '../types/api-response/helpers';

@Injectable()
export class StripeAccountsService {
    showDetailedView = false;
    data = {
        data: [],
        total: null
    };
    spinnerShow = false;
    showServiceUnavailable = false;
    selectedItems: Array<any> = [];
    serviceName: string = 'stripe_accounts';
    onStripeAccountSelected: EventEmitter<any> = new EventEmitter();
    onDetailedViewHide: EventEmitter<any> = new EventEmitter();
    onStripeBalanceReceived: EventEmitter<any> = new EventEmitter();
    selectedTab = '';
    tabId = '';
    selectedStripeAccountId: string = null;
    firstTimeLoaded = false;
    availableBalance = '';

    private stripeAccountsCache$: Observable<IStripeAccount[]>;
    private reloadStripeAccounts$ = new Subject<void>();

    constructor(
        public api: ApiClientService,
        private amenityApi: AmenityApiService,
        public urlService: UrlService,
        public emptyRowService: EmptyRowService,
        public account: AccountPermissionsService,
        private buildingApi: BuildingApiService
    ) {
        this.api.onLogout.subscribe(() => this.forceReloadStripeAccounts());
    }

    forceReloadStripeAccounts() {
        this.reloadStripeAccounts$.next();
        this.stripeAccountsCache$ = null;
    }

    get stripeAccounts$(): Observable<IStripeAccount[]> {
        if (!this.stripeAccountsCache$) {
            this.stripeAccountsCache$ = forkJoin([
                this.buildingApi.getStripeAccounts({'per-page': 1000}),
                this.amenityApi.getStripeAccounts()
            ]).pipe(
                map(([building, amenity]) => [...building.entities, ...amenity.entities]),
                takeUntil(this.reloadStripeAccounts$),
                shareReplay(1)
            );
        }

        return this.stripeAccountsCache$;
    }

    getData() {
        this.showServiceUnavailable = false;
        this.spinnerShow = true;
        this.data.total = null;

        const stripeAccountUuid = this.selectedStripeAccountId;

        const params = {'order-by': this.urlService.queryParams['order-by']};

        if(this.tabId == 'allStripeTransactions') {
            params['hasPayout'] = this.urlService.queryParams['hasPayout'] || 0;

            if(this.urlService.queryParams['periodStartDate']) {
                params['dateFrom'] = this.urlService.queryParams['periodStartDate'];
            }

            if(this.urlService.queryParams['periodEndDate']) {
                params['dateTo'] = this.urlService.queryParams['periodEndDate'];
            }
        }

        const observable: Observable<ApiResponseList<any>> = this.tabId == 'allStripeTransactions' ?
            this.amenityApi.getStripePayments(stripeAccountUuid, params) :
            this.amenityApi.getStripePayouts({...params, stripe_account_uuid: stripeAccountUuid});

        observable.subscribe(
            (result) => {
                const entities = result.entities;
                this.data.data = [];
                this.data.total = result.total;
                this.emptyRowService.fillEmptyRow();

                if (this.tabId == 'allStripeTransactions') {
                    this.transactionsFactory(entities, this.data.data);

                    if (this.showDetailedView) {
                        this.selectedItems = this.data.data.filter(item => {
                            item.checked = this.selectedItems.some(selected => selected.id == item.id);
                            return item.checked;
                        });
                        this.showDetailedView = !!this.selectedItems.length;
                    }
                } else {
                    this.data.data.push(...result.entities);
                }

                this.spinnerShow = false;
            },
            (error) => {
                this.showServiceUnavailable = true;
                this.spinnerShow = false;
                this.api.errorHandler(error, 'Failed get items');
            }
        );
    }

    transactionsFactory(entities: any, targetArray) {
        entities.forEach((item) => {
            const transaction = new StripeTransaction();

            if (item.amenityName      ) transaction.amenityName       = item.amenityName;
            if (item.amount           ) transaction.amount            = parseFloat(item.amount);
            if (item.bookingId        ) transaction.bookingId         = item.bookingId;
            if (item.bookingNumber    ) transaction.bookingNumber     = item.bookingNumber;
            if (item.buildingName     ) transaction.buildingName      = item.buildingName;
            if (item.fee              ) transaction.fee               = parseFloat(item.fee);
            if (item.id               ) transaction.id                = item.id;
            if (item.net              ) transaction.net               = parseFloat(item.net);
            if (item.paymentAtUtc     ) transaction.paymentAtUtc      = item.paymentAtUtc;
            if (item.refunded         ) transaction.refunded          = parseFloat(item.refunded);
            if (item.residentName     ) transaction.residentName      = item.residentName;
            if (item.stripeTransaction) transaction.stripeTransaction = item.stripeTransaction;
            if (item.suiteNumber      ) transaction.suiteNumber       = item.suiteNumber;
            if (item.createdAt        ) transaction.createdAt         = item.createdAt;
            if (item.hasPayout        ) transaction.hasPayout         = item.hasPayout;

            targetArray.push(transaction);
        });
    }

    resetData() {
        this.data.data = [];
        this.data.total = null;
        this.showDetailedView = false;
        this.selectedItems = [];
        this.firstTimeLoaded = false;
    }

    toggleDetailedView(data, state: boolean): void {
        if(!state) {
            this.selectedItems = [];
            this.data.data.forEach(item => item.checked = false);
            this.onDetailedViewHide.emit();
        }
        this.showDetailedView = state ? this.selectedItems.length > 0 : state;
    }

    getStripeBalance() {
        this.amenityApi.getStripeBalance(this.selectedStripeAccountId).subscribe(
            result => {
                const amount = (result.entities && result.entities[0] && result.entities[0].available) || 0;
                this.onStripeBalanceReceived.emit(amount);
            },
            error => this.api.errorHandler('Failed to get Stripe Balance', error)
        );
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/stripe-accounts.service.ts